// src/lib/chatUtils.js
// Shared helpers for the member chats and the admin inbox.

const GROUP_GAP_MS = 5 * 60 * 1000 // messages from one person within 5 min read as one group

export const isImageMessage = c => typeof c === 'string' && c.startsWith('[IMAGE]')
export const imageUrl       = c => c.replace('[IMAGE]', '')

const dayStart = d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

// "Today", "Yesterday", a weekday for the past week, otherwise a date
export function dayLabel(iso, now = new Date()) {
  const d = new Date(iso)
  const diff = Math.round((dayStart(now) - dayStart(d)) / 86400000)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  if (diff > 1 && diff < 7) return d.toLocaleDateString('en-US', { weekday: 'long' })
  return d.toLocaleDateString('en-US',
    d.getFullYear() === now.getFullYear()
      ? { month: 'long', day: 'numeric' }
      : { month: 'long', day: 'numeric', year: 'numeric' })
}

export const timeLabel = iso =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

// Turns a flat message list into rows: day dividers + messages tagged with
// whether they start / end a run of messages from the same sender.
export function withChatLayout(messages) {
  const rows = []
  messages.forEach((msg, i) => {
    const prev = messages[i - 1]
    const next = messages[i + 1]
    const t = new Date(msg.created_at).getTime()
    const day = dayStart(new Date(msg.created_at))
    const newDay = !prev || dayStart(new Date(prev.created_at)) !== day
    if (newDay) rows.push({ type: 'divider', key: `day-${msg._key || msg.id}`, label: dayLabel(msg.created_at) })

    const joinsPrev = prev && !newDay && prev.sender_id === msg.sender_id
      && t - new Date(prev.created_at).getTime() < GROUP_GAP_MS
    const joinsNext = next && next.sender_id === msg.sender_id
      && dayStart(new Date(next.created_at)) === day
      && new Date(next.created_at).getTime() - t < GROUP_GAP_MS

    rows.push({
      type: 'msg', key: msg._key || msg.id, msg,
      firstInGroup: !joinsPrev, lastInGroup: !joinsNext,
    })
  })
  return rows
}

// ── Optimistic sending ─────────────────────────────────────────
// A message shows instantly as "sending"; it's swapped for the saved copy
// on success, or flagged "failed" (with retry) instead of silently vanishing.
export function makePending({ senderId, receiverId, content }) {
  const id = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  return {
    id, _key: id, _status: 'sending',
    sender_id: senderId, receiver_id: receiverId,
    content, created_at: new Date().toISOString(),
  }
}

export const resolvePending = (list, tempId, saved) => {
  const out = []
  for (const m of list) {
    if (m.id === tempId) out.push({ ...saved, _key: m._key || tempId })
    else if (m.id !== saved.id) out.push(m) // drop a duplicate of the saved message
  }
  return out
}
export const failPending = (list, tempId) =>
  list.map(m => (m.id === tempId ? { ...m, _status: 'failed' } : m))
export const markSending = (list, tempId) =>
  list.map(m => (m.id === tempId ? { ...m, _status: 'sending' } : m))

// When a fresh copy of the history is loaded, keep any messages still in flight
export const keepUnsent = (loaded, current) =>
  [...loaded, ...current.filter(m => m._status)]

// ── Read receipts ──────────────────────────────────────────────
export const applyReadReceipt = (list, row) =>
  list.map(m => (m.id === row.id ? { ...m, read_at: row.read_at } : m))

// The id of my latest sent message, if the other side has read it
export function seenMessageId(messages, myId) {
  const lastMine = [...messages].reverse().find(m => m.sender_id === myId && !m._status)
  return lastMine && lastMine.read_at ? lastMine.id : null
}

// ── Admin inbox ────────────────────────────────────────────────
// messages: newest first. Returns each member's latest message + unread count.
// (Only counts a message unread when read_at is exactly null, so nothing
// shows as unread until the database update has been run.)
export function buildInbox(messages, adminId) {
  const last = {}
  const unread = {}
  for (const m of messages) {
    const other = m.sender_id === adminId ? m.receiver_id : m.sender_id
    if (!last[other]) last[other] = toLast(m, adminId)
    if (m.receiver_id === adminId && m.read_at === null) unread[other] = (unread[other] || 0) + 1
  }
  return { last, unread }
}

export const toLast = (m, adminId) =>
  ({ content: m.content, created_at: m.created_at, fromMe: m.sender_id === adminId })

export function previewText(last) {
  if (!last) return ''
  const body = isImageMessage(last.content) ? '📷 Photo' : (last.content || '')
  const short = body.length > 38 ? body.slice(0, 38).trimEnd() + '…' : body
  return (last.fromMe ? 'You: ' : '') + short
}

export function inboxTime(iso, now = new Date()) {
  const d = new Date(iso)
  const diff = Math.round((dayStart(now) - dayStart(d)) / 86400000)
  if (diff === 0) return timeLabel(iso)
  if (diff === 1) return 'Yesterday'
  if (diff > 1 && diff < 7) return d.toLocaleDateString('en-US', { weekday: 'short' })
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
